// src/pages/EditorTasks.tsx
import appSettings from "@/appsettings.json";
import ResponsibilityAgreementModal from "@/components/modals/ResponsibilityAgreementModal";
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { ProjectSubmission, UploadStatus } from "@/javascript/project-objects";
import { Utils } from "@/javascript/utils";
import {
	ActionIcon,
	Box,
	Button,
	Card,
	Group,
	Loader,
	ScrollArea,
	Skeleton,
	Stack,
	Table,
	Text,
	Tooltip,
	useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
	IconAlertCircle,
	IconCameraDown,
	IconCheck,
	IconChevronDown,
	IconChevronUp,
	IconDownload,
	IconExclamationMark,
	IconFileCertificate,
	IconFileMusic,
	IconFileUpload,
	IconLoader,
	IconPlayerPlay,
	IconQuestionMark,
	IconRefresh,
	IconUpload
} from "@tabler/icons-react";
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Link } from "react-router-dom";
import VideoModal from "../conduction-social/VideoModal";
import { Countdown } from "../misc/Countdown";
import { AgreementStatement } from "../modals/ResponsibilityAgreementModal";

/* ----------------------------- constants/helpers ---------------------------- */

type UploadStateKey =
	| "processing"
	| "completed"
	| "submitAgain"
	| "pastDue"
	| "notSubmitted"
	| "error";
type UploadState = {
	key: UploadStateKey;
	label: string;
	icon: (size: number) => React.ReactNode;
};

const asDate = (d: Date | string | null | undefined): Date | null => {
	if (!d) return null;
	if (d instanceof Date) return isNaN(+d) ? null : d;
	// Expecting backend to return ISO (UTC). Let Date parse natively.
	const parsed = new Date(d);
	return isNaN(+parsed) ? null : parsed;
};

// display in the user's local timezone
const toLocal = (
	d: Date | string | null | undefined,
	isMobile = false
): string => {
	const date = asDate(d);
	if (!date) return "—";

	const mobileFmt: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
	};
	const desktopFmt: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
	};
	return date.toLocaleString(undefined, isMobile ? mobileFmt : desktopFmt);
};

const computeUploadState = (
	submissionStatus: string | null | undefined,
	fulfilled: Date | string | null | undefined,
	editorDue: Date | string | null | undefined,
	isMobile: boolean
): UploadState => {
	const now = new Date();
	const due = asDate(editorDue);

	if (submissionStatus === "Uploading")
		return {
			key: "processing",
			label: "Processing",
			icon: (s) => <IconLoader size={s} />,
		};
	if (submissionStatus === "Error")
		return {
			key: "error",
			label: isMobile ? "Upload" : "Not Yet Submitted",
			icon: (s) => <IconAlertCircle size={s} />,
		};

	if (fulfilled) {
		if (due && due < now)
			return {
				key: "completed",
				label: isMobile ? "Completed" : "Upload Completed",
				icon: (s) => <IconFileUpload size={s} />,
			};
		return {
			key: "submitAgain",
			label: isMobile ? "Completed" : "Upload Completed - Submit Again?",
			icon: (s) => <IconFileUpload size={s} />,
		};
	}

	if (due && due < now)
		return {
			key: "pastDue",
			label: "Past Due",
			icon: (s) => <IconExclamationMark size={s} />,
		};

	return {
		key: "notSubmitted",
		label: isMobile ? "Upload" : "Submit Edited Video",
		icon: (s) => <IconFileUpload size={s} />,
	};
};

const injectPulseAnimation = () => {
	if (document.getElementById("pulse-keyframes")) return;
	const style = document.createElement("style");
	style.id = "pulse-keyframes";
	style.innerHTML = `
    @keyframes pulseButton {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.08); }
    }
  `;
	document.head.appendChild(style);
};

const truncate = (s: string, max = 14) =>
	s.length > max ? s.slice(0, max) + "..." : s;

/* -------------------------------- shared UI -------------------------------- */

const DownloadClipButton: React.FC<{
	enabled: boolean;
	url: string;
	filename: string;
	fz: number;
}> = ({ enabled, url, filename, fz }) => {
	const theme = useMantineTheme();
	const onClick = useCallback(() => {
		Utils.downloadBlob(url, filename, <IconDownload />, <IconAlertCircle />);
	}, [url, filename]);

	return (
		<Button
			fz={fz}
			disabled={!enabled}
			onClick={(e) => {
				e.stopPropagation();
				if (!enabled) return;
				onClick();
			}}
			variant="default"
			size="compact-sm"
			bg={theme.colors.white[1]}
			aria-label={enabled ? "Download clip" : "Clip not available"}
			leftSection={<IconCameraDown size={fz + 2} />}
		>
			{enabled ? "Clip" : "—"}
		</Button>
	);
};

const BackingTrackButton: React.FC<{
	submission: ProjectSubmission;
	fontSz: number;
	isMobile?: boolean;
}> = ({ submission, fontSz, isMobile }) => {
	const theme = useMantineTheme();
	const onClick = useCallback(() => {
		const url = `${appSettings.S3BucketBaseUrl}InstrumentalistTaskFiles/${submission.id}.mp3`;
		const file = `${submission.submissionProjectName}-BackingTrack.mp3`.replace(
			/\s+/g,
			"_"
		);
		Utils.downloadBlob(url, file, <IconDownload />, <IconAlertCircle />);
	}, [submission, fontSz]);

	return (
		<Button
			variant="default"
			size={isMobile ? "compact-xs" : "compact-sm"}
			c={theme.colors.blue[8]}
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}
			rightSection={<IconDownload size={fontSz} />}
		>
			<Tooltip
				label={
					<Text fz={fontSz}>
						This is what the musicians hear while recording their parts. It is
						provided for you as a reference.
					</Text>
				}
				position="top"
				withArrow
			>
				{isMobile ? (
					<IconFileMusic size={12} />
				) : (
					<Text fz={fontSz}>Reference Track</Text>
				)}
			</Tooltip>
		</Button>
	);
};

const ConductionLogoButton: React.FC<{
	fontSz: number;
	isMobile?: boolean;
}> = ({ fontSz, isMobile }) => {
	const theme = useMantineTheme();
	const onClick = () => {
		const url = `${appSettings.S3BucketBaseUrl}DownloadableFiles/conduction-logo.png`;
		const file = "conduction-logo.png";
		Utils.downloadBlob(url, file, <IconDownload />, <IconAlertCircle />);
	};

	return (
		<Button
			variant="default"
			size={isMobile ? "compact-xs" : "compact-sm"}
			c={theme.colors.blue[8]}
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}
			rightSection={<IconDownload size={fontSz} />}
		>
			<Tooltip
				label={
					<Text fz={fontSz}>
						This is the official Conduction logo that you can use as a watermark. You can download it here.
					</Text>
				}
				position="top"
				withArrow
			>
				{isMobile ? (
					<IconFileCertificate size={12} />
				) : (
					<Text fz={fontSz}>Conduction Logo</Text>
				)}
			</Tooltip>
		</Button>
	);
};

const UploadEditedButton: React.FC<{
	state: UploadState;
	isUploading: boolean;
	fz: number;
	onPickFile: () => void;
	isMobile?: boolean;
	disabled?: boolean;
}> = ({ state, isUploading, fz, onPickFile, isMobile, disabled }) => {
	const Btn = (
		<Tooltip
			disabled={state.key !== "processing" && !isUploading}
			label={
				state.key === "processing"
					? "This video is being processed."
					: isUploading
						? "Uploading..."
						: undefined
			}
			position="top"
			withArrow
		>
			<Button
				fz={fz}
				size={isMobile ? "compact-xs" : "compact-sm"}
				loading={state.key === "processing" || isUploading}
				loaderProps={{ type: "bars" }}
				onClick={(e) => {
					if (isUploading || disabled) return;
					e.stopPropagation();
					onPickFile();
				}}
				leftSection={<IconUpload size={fz} />}
				disabled={disabled}
			>
				<Text fz={fz}>{state.key === "completed" ? "Upload Complete!" : "Upload the Edited Video"}</Text>
			</Button>
		</Tooltip>
	);

	return state.key === "error" ? (
		<Tooltip
			label="An error occurred while uploading your video. Please try again. If the problem persists, contact support."
			position="top"
			withArrow
		>
			{Btn}
		</Tooltip>
	) : (
		Btn
	);
};

/* --------------------------- row building (merged) -------------------------- */

type RowKind = "claimed" | "offered" | "offeredClaimedOther";

type Row = {
	key: string;
	kind: RowKind;
	submission: ProjectSubmission;
};

/* ------------------------------- main component ----------------------------- */

const EditorTasks: React.FC = () => {
	const [projectSubmissions, setProjectSubmissions] = useState<ProjectSubmission[] | undefined>(
		undefined
	);
	const [isLoading, setIsLoading] =
		useState<boolean>(true);
	const [uploadingIds, setUploadingIds] = useState<string[]>([]);

	const [collapsedRows, setCollapsedRows] = useState<Set<string> | null>(null);

	const [videoModalOpened, setVideoModalOpen] = useState(false);

	// Responsibility Agreement modal state (for editors)
	const [claimAgreementOpen, setClaimAgreementOpen] =
		useState<boolean>(false);
	const [uploadAgreementOpen, setUploadAgreementOpen] =
		useState<boolean>(false);
	const [pendingSubmissionId, setPendingSubmissionId] = useState<
		string | null
	>(null);

	const theme = useMantineTheme();
	const isMobile = !!useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const fzSmall = isMobile ? 10 : 14;

	useEffect(() => {
		injectPulseAnimation();
	}, []);

	const session = useContext(SessionContext);
	if (!session) return null;

	/* ------------------------------ data loading ------------------------------ */

	const loadProjectSubmissions = useCallback(async () => {
		if (!session.user || !session.token) return;
		setIsLoading(true);
		setProjectSubmissions(undefined);
		try {
			const res = await session.getVideoEditorProjectSubmissions(session.user.id);
			setProjectSubmissions(res.items);
		} finally {
			setIsLoading(false);
		}
	}, [session]);

	useEffect(() => {
		void loadProjectSubmissions();
	}, [loadProjectSubmissions]);

	/* ------------------------------ upload edited ----------------------------- */

	const uploadEdited = useCallback(
		async (file: File, submissionId: string) => {
			const token = session.token;
			if (!token) return;

			setUploadingIds((prev) => [...prev, submissionId]);
			const shouldContinue = await Utils.confirmLargeUpload(
				file,
				500 * 1024 * 1024
			);
			if (!shouldContinue) {
				setUploadingIds((prev) =>
					prev.filter((x) => x !== submissionId)
				);
				return;
			}

			const totalBytes = file.size;
			const controller = new AbortController();
			const beforeUnload = (e: BeforeUnloadEvent) => {
				e.preventDefault();
				e.returnValue = "";
			};

			const renderProgress = (percent: number, onCancel: () => void) => (
				<div style={{ width: "100%" }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							marginBottom: 6,
						}}
					>
						<span>{percent.toFixed(0)}%</span>
						<button
							type="button"
							onClick={onCancel}
							style={{
								border: "none",
								background: "transparent",
								color: "#fa5252",
								cursor: "pointer",
								fontSize: 12,
							}}
							aria-label="Cancel upload"
						>
							Cancel upload
						</button>
					</div>
					<div
						style={{
							height: 8,
							background: "rgba(0,0,0,0.1)",
							borderRadius: 4,
							overflow: "hidden",
						}}
					>
						<div
							style={{
								height: "100%",
								width: `${Math.max(0, Math.min(100, percent))}%`,
								background: "#228be6",
								transition: "width 150ms linear",
							}}
						/>
					</div>
				</div>
			);

			const notificationId = Utils.displayNotification(
				"Uploading video... Please do not close this page or navigate away.",
				"Starting upload...",
				"blue",
				"loading",
				false,
				false
			);

			window.addEventListener("beforeunload", beforeUnload);

			let lastUpdate = 0;
			const onProgress = (uploadedBytes: number) => {
				const pct = totalBytes
					? (uploadedBytes / totalBytes) * 100
					: 0;
				const now = performance.now();
				if (now - lastUpdate < 300 && pct < 100) return;
				lastUpdate = now;

				if (pct < 100) {
					Utils.updateNotification(
						notificationId,
						"Uploading video... Please do not close this page or navigate away.",
						renderProgress(pct, () => controller.abort()),
						"blue",
						"loading",
						false,
						false
					);
				} else {
					Utils.updateNotification(
						notificationId,
						"Upload complete! We are now processing the video.",
						"You may navigate away from this page and return later.",
						"green",
						<IconFileUpload size={16} />,
						true,
						false
					);

					setTimeout(() => {
						void session.clearProjectSubmissionsCache();
						void loadProjectSubmissions();
					}, 5000);
				}
			};

			try {
				await ConductionAPI.fulfillProjectSubmission(
					submissionId,
					file,
					token,
					{
						onProgress,
						signal: controller.signal,
					}
				);

				await session.clearProjectSubmissionsCache();
				await loadProjectSubmissions();
			} catch (error) {
				const isAbort =
					error instanceof DOMException &&
					error.name === "AbortError";
				const msg =
					error instanceof Error
						? error.message
						: "Unknown error";

				if (isAbort) {
					Utils.updateNotification(
						notificationId,
						"Upload canceled",
						undefined,
						"yellow",
						<IconAlertCircle size={16} />,
						true,
						8000
					);
				} else {
					Utils.updateNotification(
						notificationId,
						"Upload Failed",
						msg ||
						"Try reducing file size or resolution, then try again.",
						"red",
						<IconAlertCircle size={16} />,
						true,
						false
					);
				}
			} finally {
				setUploadingIds((prev) =>
					prev.filter((x) => x !== submissionId)
				);
				window.removeEventListener("beforeunload", beforeUnload);
			}
		},
		[session, loadProjectSubmissions]
	);

	const pickEditedFile = useCallback(
		(submissionId: string) => {
			if (uploadingIds.includes(submissionId)) return;
			const input = document.createElement("input");
			input.type = "file";
			input.accept = ".mp4,.mov,.avi,.mkv,.webm";
			input.onchange = (e) => {
				const file = (e.target as HTMLInputElement).files?.[0];
				if (file) void uploadEdited(file, submissionId);
			};
			input.click();
		},
		[uploadingIds, uploadEdited]
	);

	/* -------------------------------- claim edit ------------------------------- */

	const claimEdit = useCallback(
		async (submissionId: string) => {
			if (!session.token) return;

			const notifId = Utils.displayNotification(
				"Claiming edit...",
				"One moment...",
				"blue",
				"loading",
				false,
				false
			);

			try {
				const res =
					await ConductionAPI.claimVideoEditorSubmission(
						session.token,
						submissionId
					);

				if (res.alreadyClaimed) {
					Utils.updateNotification(
						notifId,
						"Already claimed",
						res.message ||
						"Sorry, another editor claimed this project just before you.",
						"yellow",
						<IconAlertCircle size={16} />,
						true,
						8000
					);
				} else {
					Utils.updateNotification(
						notifId,
						"Edit claimed!",
						"You can now download clips and upload the edited video.",
						"green",
						<IconCheck size={16} />,
						true,
						6000
					);
				}

				// Refresh both feeds
				await session.clearProjectSubmissionsCache();
				void loadProjectSubmissions();
			} catch (err: any) {
				Utils.updateNotification(
					notifId,
					"Error claiming edit",
					err?.message || "Please try again.",
					"red",
					<IconAlertCircle size={16} />,
					true,
					8000
				);
			}
		},
		[session, loadProjectSubmissions]
	);

	/* ---------------------- responsibility agreement helpers ------------------- */

	const buildClaimStatements = (): AgreementStatement[] => [
		{
			key: "deadline",
			label: (
				<Text fz="sm">
					I understand that my <strong>final edited video</strong> must be
					uploaded <strong>on or before the posted editor due date</strong>.
				</Text>
			),
		},
		{
			key: "quality",
			label: (
				<Text fz="sm">
					I will watch the <Link to={""} onClick={() => setVideoModalOpen(true)}><strong>provided example video</strong></Link> as reference for how to <strong>compose the edit and include the logo watermark</strong>.
				</Text>
			),
		},
		{
			key: "sync",
			label: (
				<Text fz="sm">
					I will <strong>synchronize all of the recordings</strong> using the synchronization clicks that the musicians record.
				</Text>
			),
		},
		{
			key: "school-appropriate",
			label: (
				<Text fz="sm">
					I will ensure that <strong>all visuals</strong> in
					the video are <strong>school-appropriate</strong> and respectful.
				</Text>
			),
		},
	];

	const buildUploadStatements = (): AgreementStatement[] => [
		{
			key: "full-review",
			label: (
				<Text fz="sm">
					I have watched the <strong>entire edited video</strong> from start to
					finish and confirmed there are no missing sections or glitches.
				</Text>
			),
		},
		{
			key: "audio-balance",
			label: (
				<Text fz="sm">
					The audio is <strong>balanced</strong> (no extreme volume jumps, big differences between instrument volumes,
					clipping, or long unwanted silences).
				</Text>
			),
		},
		{
			key: "final-export",
			label: (
				<Text fz="sm">
					The file I am uploading is the <strong>final export</strong> that I intend the
					creator, classroom, and performers to see (not a draft or test
					version).
				</Text>
			),
		},
	];

	const handleRequestClaimEdit = useCallback(
		(submissionId: string) => {
			setPendingSubmissionId(submissionId);
			setClaimAgreementOpen(true);
		},
		[]
	);

	const handleRequestUploadEdited = useCallback(
		(submissionId: string) => {
			setPendingSubmissionId(submissionId);
			setUploadAgreementOpen(true);
		},
		[]
	);

	const handleConfirmClaimAgreement = useCallback(() => {
		if (!pendingSubmissionId) return;
		void claimEdit(pendingSubmissionId);
		setClaimAgreementOpen(false);
		setPendingSubmissionId(null);
	}, [pendingSubmissionId, claimEdit]);

	const handleConfirmUploadAgreement = useCallback(() => {
		if (!pendingSubmissionId) return;
		pickEditedFile(pendingSubmissionId);
		setUploadAgreementOpen(false);
		setPendingSubmissionId(null);
	}, [pendingSubmissionId, pickEditedFile]);

	const handleCloseClaimAgreement = useCallback(() => {
		setClaimAgreementOpen(false);
		setPendingSubmissionId(null);
	}, []);

	const handleCloseUploadAgreement = useCallback(() => {
		setUploadAgreementOpen(false);
		setPendingSubmissionId(null);
	}, []);

	/* ----------------------------- merge & counters ---------------------------- */

	const rows: Row[] = useMemo(() => {
		const unsortedRows: Row[] = [];
		const currentUserId = session.user?.id;

		if (projectSubmissions) {
			for (const s of projectSubmissions) {
				const assignedEditor = s.videoEditor;

				if (assignedEditor == null) {
					// No editor assigned - this is an open offer
					unsortedRows.push({
						key: `offered-${s.id}`,
						kind: "offered",
						submission: s
					});
				} else if (assignedEditor.id === currentUserId) {
					unsortedRows.push({
						key: `claimed-${s.id}`,
						kind: "claimed",
						submission: s
					});
				} else {
					// This offer has been claimed by another editor
					unsortedRows.push({
						key: `offeredClaimedOther-${s.id}`,
						kind: "offeredClaimedOther",
						submission: s
					});
				}
			}
		}

		const kindRank = (kind: string): number => {
			switch (kind) {
				case "offered":             // unclaimed offers at the top
					return 0;
				case "claimed":             // claimed by me
					return 1;
				case "offeredClaimedOther": // claimed by someone else, show but last
					return 2;
				default:
					return 3;
			}
		};

		const statusRank = (uploadStatus: UploadStatus): number => {
			switch (uploadStatus) {
				case "Error":
					return 0;
				case "NotUploaded":
					return 1;
				case "Uploading":
					return 2;
				case "Uploaded":
					return 3;
				default:
					return 4;
			}
		};

		const sortedRows = [...unsortedRows].sort((a, b) => {
			// 1) Primary sort: by kind
			const kindDiff = kindRank(a.kind) - kindRank(b.kind);
			if (kindDiff !== 0) return kindDiff;

			const statusDiff = statusRank(a.submission.submissionStatus) - statusRank(b.submission.submissionStatus);
			if (statusDiff !== 0) return statusDiff;

			// 2) Secondary sort: by relevant date
			const aSortDate = a.kind === "claimed"
				? a.submission.editorDueDate
				: a.submission.currentOfferDeadline;

			const bSortDate = b.kind === "claimed"
				? b.submission.editorDueDate
				: b.submission.currentOfferDeadline;

			const ta = aSortDate ? +aSortDate : Number.MAX_SAFE_INTEGER;
			const tb = bSortDate ? +bSortDate : Number.MAX_SAFE_INTEGER;

			if (ta !== tb) return ta - tb;

			// 3) Tertiary sort: by project name
			const an = a.submission.submissionProjectName || "";
			const bn = b.submission.submissionProjectName || "";
			return an.localeCompare(bn);
		});

		// If this is the first load (the collapsedRows state is null), set all rows to collapsed except the first claimed
		if (collapsedRows === null && sortedRows.length > 0) {
			const newCollapsed = new Set<string>();
			let firstClaimedFound = false;
			for (const row of sortedRows) {
				if (row.kind === "claimed") {
					if (firstClaimedFound) {
						newCollapsed.add(row.key);
					} else {
						firstClaimedFound = true;
					}
				}
			}
			setCollapsedRows(newCollapsed);
		}

		return sortedRows;
	}, [projectSubmissions, session.user?.id]);

	const unfinishedEditsCount = useMemo(
		() => rows == null ? null : rows.filter((row) => row.kind === "claimed" && !row.submission.dateFulfilled).length,
		[rows]
	);

	const openOffersCount = useMemo(
		() => rows == null ? null : rows.filter((row) => row.kind === "offered").length,
		[rows]
	);

	/* --------------------------------- skeletons ------------------------------- */

	const skeletonRows = useMemo(() => {
		const row = (
			<Table.Tr>
				<Table.Td>
					<Skeleton height={74} ta="center" />
				</Table.Td>
				<Table.Td>
					<Skeleton height={74} ta="center" />
				</Table.Td>
				<Table.Td>
					<Skeleton height={74} ta="center" />
				</Table.Td>
				<Table.Td>
					<Skeleton height={74} ta="center" />
				</Table.Td>
			</Table.Tr>
		);
		return Array.from({ length: 4 }, (_, i) =>
			React.cloneElement(row, { key: `sk-${i}` })
		);
	}, []);

	/* -------------------------------- row renderers ---------------------------- */

	const ClaimedRow: React.FC<{
		s: ProjectSubmission;
		onRequestUploadEdited: (submissionId: string) => void;
		collapsed: boolean;
		toggleCollapse: () => void;
	}> = ({ s, onRequestUploadEdited, collapsed, toggleCollapse }) => {
		const state = computeUploadState(
			s.submissionStatus,
			s.dateFulfilled,
			s.editorDueDate,
			isMobile
		);
		const dueLocal = toLocal(s.editorDueDate, isMobile);

		const userName = s.projectOwner?.username || "N/A User";
		const projectName = s.submissionProjectName || "N/A Project";

		// list of instrumentalist tasks (clips)
		const tasks = s.instrumentalistTasks || [];

		const allClipsIn = tasks.every(
			(t) => t.taskStatus === "Uploaded"
		);

		const isPastDue =
			asDate(s.editorDueDate) &&
			asDate(s.editorDueDate)! < new Date();

		const rowBg = s.submissionStatus === "Uploaded" ? (
			theme.colors.green[1]
		) : (isPastDue || s.submissionStatus === "Error" ? (
			theme.colors.red[1]
		) : (
			theme.colors.yellow[1]
		));

		return (
			<>
				<Table.Tr key={`claimed-${s.id}`} bg={rowBg} onClick={toggleCollapse}>
					{/* Deadline (with countdown if not completed) */}
					<Table.Td ta="center" fz={fzSmall}>
						{s.dateFulfilled ? (
							<Text fz={fzSmall}>{dueLocal}</Text>
						) : (
							<Stack align="center">
								{!isMobile && (
									<Text fz={fzSmall}>{dueLocal}</Text>
								)}
								<Countdown
									targetDate={s.editorDueDate}
									textSz={fzSmall}
								/>
							</Stack>
						)}
					</Table.Td>

					{/* Project */}
					<Table.Td ta="center" fz={fzSmall}>
						{isMobile ? (
							<Text fz={fzSmall}>{projectName}</Text>
						) : (
							<Stack align="center" gap={2}>
								<Text fz={fzSmall} fw={700}>
									{projectName}
								</Text>
								<Text fz={fzSmall}>by {userName}</Text>
							</Stack>
						)}
					</Table.Td>

					{/* Reference Audio */}
					<Table.Td ta="center" fz={fzSmall}>
						<Stack maw={isMobile ? 60 : 160} mx="auto" gap="xs">
							<BackingTrackButton
								submission={s}
								fontSz={fzSmall}
								isMobile={isMobile}
							/>
							<ConductionLogoButton
								fontSz={fzSmall}
								isMobile={isMobile}
							/>
						</Stack>
					</Table.Td>

					{/* Action */}
					<Table.Td ta="center" fz={fzSmall}>
						<Tooltip
							label={
								<Stack gap="xs">
									<Text fz={fzSmall}>
										You have not yet received all the
										recordings from musicians.
									</Text>
									<Text fz={fzSmall}>
										The upload button will become active once
										all clips are in.
									</Text>
									<Text fz={fzSmall}>
										At that point, you can edit them all
										together and upload the final video.
									</Text>
								</Stack>
							}
							withArrow
							disabled={allClipsIn}
							position="top"
						>
							<Box>
								<UploadEditedButton
									state={state}
									isUploading={uploadingIds.includes(s.id)}
									fz={fzSmall}
									onPickFile={() =>
										onRequestUploadEdited(s.id)
									}
									isMobile={isMobile}
									disabled={!allClipsIn}
								/>
							</Box>
						</Tooltip>
					</Table.Td>

					<Table.Td
						ta="center"
						fz={fzSmall}
					>
						<Tooltip
							label="Show contents"
							position="top"
							withArrow
						>
							{collapsed ? (
								<IconChevronDown size={24} />
							) : (
								<IconChevronUp size={24} />
							)}
						</Tooltip>
					</Table.Td>
				</Table.Tr>

				{/* Inline sub-table of instrumentalist clips */}
				{collapsed ? null : (
					<Table.Tr key={`claimed-sub-${s.id}`}>
						<Table.Td ta="center">{/* Empty cell for spacing */}</Table.Td>
						<Table.Td colSpan={5} p={0}>
							<Box
								style={{
									overflowX: "auto",
									width: "100%",
								}}
							>
								<Table w="100%">
									<Table.Thead>
										<Table.Tr>
											<Table.Th ta="center">
												<Text fz={fzSmall} fw={700}>
													Instrument
												</Text>
											</Table.Th>
											<Table.Th ta="center">
												<Text fz={fzSmall} fw={700}>
													Performer
												</Text>
											</Table.Th>
											<Table.Th ta="center">
												<Text fz={fzSmall} fw={700}>
													Recordings
												</Text>
											</Table.Th>
											<Table.Th ta="center" />
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>
										{tasks.length === 0 ? (
											<Table.Tr>
												<Table.Td colSpan={5}>
													<Text
														ta="center"
														fz={fzSmall}
														c="dimmed"
													>
														No instrumentalist clips yet.
													</Text>
												</Table.Td>
											</Table.Tr>
										) : (
											tasks.map((t) => {
												const clipUrl = `${appSettings.S3BucketBaseUrl}InstrumentalistVideos/${t.id}.mp4`;
												const fname = `${s.submissionProjectName}-${t.instrument.name}.mp4`.replace(
													/\s+/g,
													"_"
												);

												return (
													<Table.Tr key={t.id}>
														<Table.Td
															ta="center"
															fz={fzSmall}
														>
															<Text fz={fzSmall}>
																{isMobile
																	? truncate(
																		t.instrument
																			.name
																	)
																	: t.instrument.name}
															</Text>
														</Table.Td>
														<Table.Td
															ta="center"
															fz={fzSmall}
														>
															{t.instrumentalist ? (
																<Text fz={fzSmall}>
																	{
																		isMobile
																			? truncate(
																				t
																					.instrumentalist
																					.username
																			)
																			: `${t.instrumentalist.username} (${t.instrumentalist.email})`
																	}
																</Text>
															) : (
																<Text
																	fz={fzSmall}
																	c="dimmed"
																>
																	Not yet assigned
																</Text>
															)}
														</Table.Td>
														<Table.Td
															ta="center"
															fz={fzSmall}
														>
															{!t.dateFulfilled ? (
																<Text
																	fz={fzSmall}
																	c="dimmed"
																>
																	Not yet uploaded
																</Text>
															) : (
																<DownloadClipButton
																	enabled={
																		!!t.dateFulfilled
																	}
																	url={clipUrl}
																	filename={fname}
																	fz={fzSmall}
																/>
															)}
														</Table.Td>
													</Table.Tr>
												);
											})
										)}
									</Table.Tbody>
								</Table>
							</Box>
						</Table.Td>
					</Table.Tr>
				)}
			</>
		);
	};

	const OfferedRow: React.FC<{
		s: ProjectSubmission;
		onRequestClaimEdit: (submissionId: string) => void;
	}> = ({ s, onRequestClaimEdit }) => {
		const projectName = s.submissionProjectName || "N/A Project";
		const userName = s.projectOwner?.username || "N/A User";
		const offerDeadline: Date | string | null | undefined = (
			s as any
		).currentOfferDeadline;
		const offerLocal = toLocal(offerDeadline, isMobile);

		const [showTooltip, setShowTooltip] = useState(false);

		return (
			<Table.Tr key={`offered-${s.id}`} bg={theme.white}>
				{/* Offer/Deadline with countdown */}
				<Table.Td ta="center" fz={fzSmall}>
					<Stack align="center">
						{!isMobile && (
							<Text fz={fzSmall}>{offerLocal}</Text>
						)}
						<Countdown
							targetDate={offerDeadline}
							textSz={fzSmall}
						/>
					</Stack>
				</Table.Td>

				{/* Project */}
				<Table.Td ta="center" fz={fzSmall}>
					{isMobile ? (
						<Text fz={fzSmall}>{projectName}</Text>
					) : (
						<Stack align="center" gap={2}>
							<Text fz={fzSmall} fw={700}>
								{projectName}
							</Text>
							<Text fz={fzSmall}>by {userName}</Text>
						</Stack>
					)}
				</Table.Td>

				{/* Files */}
				<Table.Td ta="center" fz={fzSmall}>
					<BackingTrackButton
						submission={s}
						fontSz={fzSmall}
						isMobile={isMobile}
					/>
				</Table.Td>

				{/* Action: Claim */}
				<Table.Td ta="center" fz={fzSmall}>
					<Stack align="center">
						<Button
							variant="filled"
							size={isMobile ? "compact-xs" : "compact-sm"}
							color="green"
							onClick={(e) => {
								e.stopPropagation();
								onRequestClaimEdit(s.id);
							}}
							style={{
								animation:
									"pulseButton 1.8s ease-in-out infinite",
								transformOrigin: "center",
							}}
							leftSection={
								!isMobile && <IconCheck size={fzSmall} />
							}
						>
							<Text fz={fzSmall}>Claim $30 Task!</Text>
						</Button>

						<Tooltip
							label={
								<Stack gap={2}>
									<Text fz={fzSmall}>
										After this timer runs out, the project
										will be offered to another group of
										editors.
									</Text>
									<Text fz={fzSmall}>
										Claim now to secure this project for
										yourself.
									</Text>
								</Stack>
							}
							position="bottom"
							withArrow
							opened={showTooltip}
						>
							<Group
								onClick={() =>
									setShowTooltip(!showTooltip)
								}
								gap="sm"
							>
								<Box w={45}>
									<Countdown
										targetDate={offerDeadline}
										activeTimer
										textSz={12}
										pastDueText="0:00"
									/>
								</Box>
								<ActionIcon
									c="gray"
									variant="outline"
									size="xs"
								>
									<IconQuestionMark />
								</ActionIcon>
							</Group>
						</Tooltip>
					</Stack>
				</Table.Td>
			</Table.Tr>
		);
	};

	// Disabled row mirroring Instrumentalist “claimed by other” state
	const OfferedClaimedOtherRow: React.FC<{ s: ProjectSubmission }> = ({
		s,
	}) => {
		const projectName = s.submissionProjectName || "N/A Project";
		const userName = s.projectOwner?.username || "N/A User";
		const offerDeadline: Date | string | null | undefined = (
			s as any
		).currentOfferDeadline;
		const offerLocal = toLocal(offerDeadline, isMobile);

		return (
			<Table.Tr
				key={`offered-claimed-${s.id}`}
				bg={theme.colors.gray[3]}
				opacity={0.5}
			>
				{/* Deadline (static + countdown for consistency) */}
				<Table.Td ta="center" fz={fzSmall}>
					<Stack align="center">
						{!isMobile && (
							<Text fz={fzSmall}>{offerLocal}</Text>
						)}
						<Countdown
							targetDate={offerDeadline}
							textSz={fzSmall}
						/>
					</Stack>
				</Table.Td>

				{/* Project */}
				<Table.Td ta="center" fz={fzSmall}>
					{isMobile ? (
						<Text fz={fzSmall}>{projectName}</Text>
					) : (
						<Stack align="center" gap={2}>
							<Text fz={fzSmall} fw={700}>
								{projectName}
							</Text>
							<Text fz={fzSmall}>by {userName}</Text>
						</Stack>
					)}
				</Table.Td>

				{/* Files */}
				<Table.Td ta="center" fz={fzSmall}>
					<Text fz={fzSmall} c="dimmed">
						—
					</Text>
				</Table.Td>

				{/* Action: disabled message */}
				<Table.Td ta="center" fz={fzSmall}>
					<Tooltip
						label="Another editor has already claimed this project. Try claiming other offers."
						position="bottom"
						withArrow
					>
						<Text fz={fzSmall} c={theme.colors.red[7]}>
							Claimed by another editor.
						</Text>
					</Tooltip>
				</Table.Td>
			</Table.Tr>
		);
	};

	/* ----------------------------------- render -------------------------------- */

	return (
		<Box
			p="xs"
			w="100%"
			h="100%"
			style={{
				display: "grid",
				gridTemplateRows: "auto 1fr auto",
				overflow: "hidden",
				minHeight: 0,
			}}
		>
			{/* Header */}
			<Group
				w="100%"
				justify="space-between"
				align="center"
				p="xs"
			>
				<Stack gap={4}>
					<Text c={theme.colors.blue[0]} fz={fzSmall}>
						Download instrumentalist clips, edit the final project video,
						and submit here.
					</Text>

					<Group gap="xs">
						<Text c={theme.colors.blue[0]} fz={fzSmall}>
							Watch an example video:
						</Text>
						<Tooltip label="Click here to watch an example video">
							<Button
								onClick={() => setVideoModalOpen(true)}
								size={isMobile ? "compact-xs" : "compact-sm"}
								variant="default"
							>
								<IconPlayerPlay size={isMobile ? 14 : 16} />
							</Button>
						</Tooltip>
					</Group>

					<VideoModal
						title="Example Video"
						videoPath={`${appSettings.S3BucketBaseUrl}Videos/ExampleVideo.mp4`}
						isOpen={videoModalOpened}
						setShowModal={setVideoModalOpen}
					/>
				</Stack>

				<Group
					w="100%"
					maw="520px"
					justify="space-between"
					align="center"
					p="xs"
				>
					{isLoading ? (
						<Group>
							<Loader
								size={fzSmall * 2}
								color={theme.colors.blue[0]}
							/>
							<Card
								withBorder
								p="xs"
								bg={theme.colors.blue[1]}
								w={fzSmall * 18}
								h={fzSmall * 5}
								padding="xs"
							/>
						</Group>
					) : (
						<Group>
							{unfinishedEditsCount === null || unfinishedEditsCount > 0 ||
								openOffersCount === null || openOffersCount > 0 ? (
								<IconAlertCircle
									size={fzSmall * 2}
									color={theme.colors.yellow[6]}
								/>
							) : (
								<IconCheck
									size={fzSmall * 2}
									color={theme.colors.green[6]}
								/>
							)}
							<Card
								withBorder
								p="xs"
								bg={theme.colors.blue[1]}
								w={fzSmall * 18}
								h={fzSmall * 5}
								padding="xs"
							>
								<Stack gap={2}>
									{unfinishedEditsCount === null ? (
										<Text
											c={theme.colors.blue[12]}
											fz={fzSmall}
										>
											Could not load task status.
										</Text>
									) : (
										unfinishedEditsCount > 0 ? (
											<Text
												c={theme.colors.blue[12]}
												fz={fzSmall}
											>
												You have{" "}
												<strong>
													{unfinishedEditsCount} unfinished
													edit
													{unfinishedEditsCount === 1
														? ""
														: "s"}
												</strong>
												.
											</Text>
										) : (
											<Text
												c={theme.colors.blue[12]}
												fz={fzSmall}
											>
												All edits completed!
											</Text>
										)
									)}

									<Text
										c={theme.colors.blue[12]}
										fz={fzSmall}
									>
										You have{" "}
										<strong>
											{openOffersCount} unclaimed offer
											{openOffersCount === 1 ? "" : "s"}
										</strong>
										.
									</Text>
								</Stack>
							</Card>
						</Group>
					)}

					<Button
						leftSection={<IconRefresh size={fzSmall} />}
						onClick={() => {
							void session.clearProjectSubmissionsCache();
							setIsLoading(true);
							void loadProjectSubmissions();
						}}
						c={theme.colors.blue[0]}
						disabled={isLoading}
					>
						<Text fz={fzSmall}>Refresh</Text>
					</Button>
				</Group>
			</Group>

			{/* Body */}
			<ScrollArea
				h="100%"
				w="100%"
				bg={theme.colors.blue[0]}
				type="auto"
				viewportProps={{ style: { height: "100%" } }}
				styles={{ viewport: { overflowX: "hidden" } }}
			>
				<Table
					w="100%"
					h="100%"
					striped
					highlightOnHover
					stickyHeader
				>
					<Table.Thead>
						<Table.Tr>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Deadline</Text>
							</Table.Th>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Project</Text>
							</Table.Th>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>General Files</Text>
							</Table.Th>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Action</Text>
							</Table.Th>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Collapse</Text>
							</Table.Th>
						</Table.Tr>
					</Table.Thead>

					<Table.Tbody>
						{isLoading ? (
							skeletonRows
						) : rows.length === 0 ? (
							<Table.Tr>
								<Table.Td colSpan={5}>
									<Text ta="center">
										No edits or offers right now
									</Text>
								</Table.Td>
							</Table.Tr>
						) : (
							rows.flatMap((mr) =>
								mr.kind === "claimed"
									? [
										<ClaimedRow
											key={mr.key}
											s={mr.submission}
											onRequestUploadEdited={
												handleRequestUploadEdited
											}
											collapsed={collapsedRows?.has(mr.key) ?? false}
											toggleCollapse={() => {
												if (collapsedRows?.has(mr.key)) {
													// Remove from collapsed
													setCollapsedRows((prev) => {
														const newSet = new Set(
															prev
														);
														newSet.delete(mr.key);
														return newSet;
													});
												} else {
													setCollapsedRows((prev) => {
														const newSet = new Set(
															prev
														);
														newSet.add(mr.key);
														return newSet;
													});
												}
											}}
										/>,
									]
									: mr.kind === "offered"
										? [
											<OfferedRow
												key={mr.key}
												s={mr.submission}
												onRequestClaimEdit={
													handleRequestClaimEdit
												}
											/>,
										]
										: [
											<OfferedClaimedOtherRow
												key={mr.key}
												s={mr.submission}
											/>,
										]
							)
						)}
					</Table.Tbody>
				</Table>
			</ScrollArea>

			{/* Responsibility Agreement Modal for claiming edit projects */}
			<ResponsibilityAgreementModal
				titleText="Responsibility Agreement"
				promptText="Before you claim this editing project, please read each statement and check all boxes to confirm your agreement."
				buttonText="Claim Project"
				opened={claimAgreementOpen}
				onClose={handleCloseClaimAgreement}
				onConfirm={handleConfirmClaimAgreement}
				statements={buildClaimStatements()}
			/>

			{/* Responsibility Agreement Modal for uploading edited videos */}
			<ResponsibilityAgreementModal
				titleText="Final Edit Checklist"
				promptText="Before uploading your edited video, please read each statement and check each box to confirm your final review."
				buttonText="Proceed to Upload"
				opened={uploadAgreementOpen}
				onClose={handleCloseUploadAgreement}
				onConfirm={handleConfirmUploadAgreement}
				statements={buildUploadStatements()}
			/>
		</Box>
	);
};

export default EditorTasks;
